import moment, { type Moment, type unitOfTime } from 'moment';

/**
 * Parse a time string like '16:30' or '4:30 PM' into a moment
 */
export function timeToMoment(timeString: string) {
  let format = 'h';
  if (timeString.match(/^\d{2}/)) {
    format += 'h';
  }
  if (timeString.includes(':')) {
    format += ':mm';
  }
  if (timeString.toUpperCase().match(/AM|PM/)) {
    format += ' a';
  }
  return moment(timeString, format);
}

/**
 * Format a moment into a time string like '4:30 PM'
 */
export function formatTime(time: Moment, defaultTime?: string) {
  const localTime = setMoments(moment(), time, ['hour', 'minute']);
  return localTime
    .toDate()
    .toLocaleTimeString(navigator.language, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    })
    .replace(' AM', 'AM')
    .replace(' PM', 'PM')
    .toUpperCase();
}

/**
 * Given two Moment objects and a list of units, set the units of the target
 * to match the source.
 *
 * Example, copy the time from one moment to another:
 *
 * ```
 * const target = moment();
 * const source = moment();
 * setMoments(target, source, ['hour', 'minute', 'second']);
 * ```
 */
export function setMoments(
  target: Moment,
  source: Moment,
  units: unitOfTime.All[]
) {
  units.forEach(unit => {
    target.set(unit, source.get(unit));
  });
  return target;
}

/**
 * Copy the date from one moment to another while leaving the time intact.
 */
export function setMomentDate(time: Moment, date: Moment) {
  return setMoments(time.clone().utcOffset(time.utcOffset()), date, [
    'year',
    'month',
    'date',
  ]);
}

/**
 * Copy the time from one moment to another while leaving the date intact.
 */
export function setMomentTime(date: Moment, time: Moment) {
  return setMoments(date.clone(), time, ['hour', 'minute']).startOf('minute');
}
